import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";

const LANGS = ["ru", "uz", "en"];
const EMPTY = { name: "", title: {}, description: {}, button_text: {}, link: "", image_url: "", dark: false, photo_only: false, active: true };

export default function Ads() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/ads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((e) => ({ ...e, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Фото загружено");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Ошибка загрузки");
    }
    setUploading(false);
  };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/ads/${edit.id}`, edit);
      else await api.post("/admin/ads", edit);
      toast.success("Сохранено");
      setEdit(null); load();
    } catch (e) { toast.error("Ошибка"); }
  };
  const del = async (a) => { if (window.confirm("Удалить баннер?")) { await api.delete(`/admin/ads/${a.id}`); load(); } };
  const toggle = async (a) => { await api.put(`/admin/ads/${a.id}`, { active: !a.active }); load(); };

  return (
    <div className="space-y-4" data-testid="admin-ads">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl">Рекламные баннеры</h1>
        <button data-testid="ad-create-btn" className="admin-btn" onClick={() => setEdit({ ...EMPTY })}><Plus size={16} /> Баннер</button>
      </div>
      {edit && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2.5" data-testid="ad-edit-form">
          <input data-testid="ad-name-input" className="admin-input" placeholder="Название (внутреннее)" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          <div className="grid md:grid-cols-3 gap-2">
            {LANGS.map((l) => <input key={l} className="admin-input" placeholder={`Заголовок (${l.toUpperCase()})`} value={edit.title?.[l] || ""} onChange={(e) => setEdit({ ...edit, title: { ...edit.title, [l]: e.target.value } })} />)}
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {LANGS.map((l) => <input key={l} className="admin-input" placeholder={`Описание (${l.toUpperCase()})`} value={edit.description?.[l] || ""} onChange={(e) => setEdit({ ...edit, description: { ...edit.description, [l]: e.target.value } })} />)}
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {LANGS.map((l) => <input key={l} className="admin-input" placeholder={`Кнопка (${l.toUpperCase()})`} value={edit.button_text?.[l] || ""} onChange={(e) => setEdit({ ...edit, button_text: { ...edit.button_text, [l]: e.target.value } })} />)}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <label className="block"><span className="text-xs font-bold text-gray-500">Ссылка (весь баннер кликабелен)</span>
              <input data-testid="ad-link-input" className="admin-input" placeholder="https://..." value={edit.link} onChange={(e) => setEdit({ ...edit, link: e.target.value })} /></label>
            <label className="block"><span className="text-xs font-bold text-gray-500">Красный тег (например: 18 000 СУМ)</span>
              <input data-testid="ad-tag-input" className="admin-input" placeholder="18 000 СУМ" value={edit.tag || ""} onChange={(e) => setEdit({ ...edit, tag: e.target.value })} /></label>
          </div>
          <div className="space-y-1.5">
            <span className="text-xs font-bold text-gray-500">Фото баннера</span>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
              <button data-testid="ad-upload-btn" type="button" className="admin-btn admin-btn-ghost" disabled={uploading} onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> {uploading ? "Загрузка..." : "Загрузить фото"}
              </button>
              <input data-testid="ad-image-input" className="admin-input flex-1" placeholder="...или вставьте URL изображения" value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} />
            </div>
            {edit.image_url && <img src={edit.image_url} alt="" className="h-24 rounded-xl object-cover" />}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
            <label className="text-xs font-bold flex gap-1.5 items-center"><input type="checkbox" checked={edit.dark} onChange={(e) => setEdit({ ...edit, dark: e.target.checked })} /> Тёмный стиль</label>
            <label className="text-xs font-bold flex gap-1.5 items-center"><input data-testid="ad-photo-only-toggle" type="checkbox" checked={!!edit.photo_only} onChange={(e) => setEdit({ ...edit, photo_only: e.target.checked })} /> Только фото (без текста и эффектов)</label>
            <label className="text-xs font-bold flex gap-1.5 items-center"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Активен</label>
            <div className="w-full sm:w-auto sm:ml-auto flex gap-2">
              <button className="admin-btn admin-btn-ghost flex-1 sm:flex-none" onClick={() => setEdit(null)}>Отмена</button>
              <button data-testid="ad-save-btn" className="admin-btn flex-1 sm:flex-none" disabled={!edit.name} onClick={save}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`ad-card-${a.id}`}>
            <div className={`relative h-32 ${a.dark ? "bg-[#121212]" : "bg-[#FEBC10]"}`}>
              {a.image_url && <img src={a.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />}
              <div className={`absolute inset-0 p-4 flex flex-col justify-end ${a.dark ? "bg-gradient-to-t from-black/80 to-transparent text-white" : "bg-gradient-to-t from-[#FEBC10]/90 to-transparent"}`}>
                <div className="font-display font-black text-sm">{a.title?.ru}</div>
              </div>
            </div>
            <div className="p-3.5 space-y-2">
              <div className="font-bold text-sm">{a.name}</div>
              <div className="flex gap-3 text-xs text-gray-500 font-semibold">
                <span>Показы: {a.impressions || 0}</span><span>Клики: {a.clicks || 0}</span><span>CTR: {a.ctr}%</span>
              </div>
              <div className="flex gap-2">
                <button data-testid={`ad-toggle-${a.id}`} onClick={() => toggle(a)} className={`px-2.5 py-1 rounded-full text-xs font-bold ${a.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{a.active ? "ON" : "OFF"}</button>
                <button data-testid={`ad-edit-${a.id}`} onClick={() => setEdit(a)} className="p-1.5 bg-gray-100 rounded-lg"><Pencil size={13} /></button>
                <button onClick={() => del(a)} className="p-1.5 bg-gray-100 rounded-lg text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
